import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { BarChart3, Building2, Radar, Flag, Tags, Megaphone, Menu, ArrowLeft, Users, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Logo } from '@/components/brand/Logo';
import { SideNav } from '@/pages/dashboard/DashboardLayout';
import { useData } from '@/hooks/useData';
import { reportService } from '@/services/api';
import { useSeo } from '@/lib/seo';

export default function AdminLayout() {
  useSeo({ title: 'Administração' });
  const { user, authLoading } = useData();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openReports, setOpenReports] = useState(0);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    reportService
      .list()
      .then((list) => setOpenReports(list.filter((r) => r.status === 'aberta').length))
      .catch(() => setOpenReports(0));
  }, [user]);

  if (authLoading) {
    return <div className="container-x py-16 text-center text-sm text-muted-foreground">Carregando…</div>;
  }

  if (!user) {
    return <Navigate to="/entrar" state={{ from: location.pathname }} replace />;
  }

  if (user.role !== 'admin') {
    return (
      <div className="container-x py-16">
        <div className="surface mx-auto max-w-lg p-8 text-center" data-testid="admin-forbidden">
          <ShieldAlert className="mx-auto h-8 w-8 text-[rgb(255,130,150)]" />
          <h1 className="mt-3 text-xl font-semibold">Acesso restrito</h1>
          <p className="mt-2 text-sm text-muted-foreground">Esta área é exclusiva da administração da plataforma.</p>
          <div className="mt-5 flex justify-center gap-2">
            <Button asChild className="btn-glow"><Link to="/painel">Ir para o painel</Link></Button>
            <Button asChild variant="secondary" className="border border-white/10"><Link to="/">Voltar ao site</Link></Button>
          </div>
        </div>
      </div>
    );
  }

  const NAV = [
    { to: '/admin', label: 'Indicadores', icon: BarChart3, end: true },
    { to: '/admin/empresas', label: 'Empresas', icon: Building2 },
    { to: '/admin/oportunidades', label: 'Oportunidades', icon: Radar },
    { to: '/admin/denuncias', label: `Denúncias${openReports ? ` (${openReports})` : ''}`, icon: Flag },
    { to: '/admin/contas', label: 'Contas', icon: Users },
    { to: '/admin/categorias', label: 'Categorias', icon: Tags },
    { to: '/admin/patrocinados', label: 'Anúncios patrocinados', icon: Megaphone },
  ];

  const inner = (
    <div className="flex h-full flex-col">
      <Link to="/" className="px-2"><Logo /></Link>
      <div className="mt-4 rounded-md border border-[rgba(255,59,92,0.25)] bg-[rgba(255,59,92,0.06)] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[rgb(255,150,165)]">Moderação</div>
      <div className="mt-5"><SideNav items={NAV} testId="admin-sidebar-nav" /></div>
      <Button asChild variant="ghost" size="sm" className="mt-auto justify-start text-muted-foreground"><Link to="/"><ArrowLeft /> Voltar ao site</Link></Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--void)]">
      <aside className="hidden border-r border-white/10 bg-[#07090d] p-4 lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">{inner}</aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-white/10 bg-[rgba(7,8,11,0.85)] px-4 backdrop-blur-md sm:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" aria-label="Abrir menu administrativo" data-testid="admin-sidebar-open-button"><Menu /></Button></SheetTrigger>
            <SheetContent side="left" className="w-[280px] border-white/10 bg-[#07090d] p-4" onClick={(e) => e.target.closest('a') && setOpen(false)}>
              <SheetHeader className="sr-only"><SheetTitle>Menu administrativo</SheetTitle></SheetHeader>
              {inner}
            </SheetContent>
          </Sheet>
          <div className="font-display text-sm font-semibold">Administração</div>
        </header>
        <main className="px-4 py-6 sm:px-6 lg:px-8"><Outlet /></main>
      </div>
    </div>
  );
}